<template>
    <!-- App.vue -->
    <v-app>
        <v-main>
            <!-- App Error / Loading -->
            <v-container v-if="!isAuthed || appError">
                <!-- Base Error -->
                <base-error v-if="appError" :error="appError">
                    <template v-slot:bottom>
                        <v-btn :href="$route.fullPath" color="primary">
                            <v-icon class="mr-3">mdi-refresh</v-icon>
                            Reload Page
                        </v-btn>
                    </template>
                </base-error>
                <!-- Base App Loading -->
                <base-app-loading v-else-if="loading"></base-app-loading>
            </v-container>
            <!-- Main App -->
            <template v-else>
                <!-- Connection Monitor-->
                <base-dialog :max-width="500" :value="hasInitConnections && hasLostConnection && !hasMaxedAttempts">
                    <base-connection-monitor
                        :connections="connectionsFormatted"
                    ></base-connection-monitor>
                </base-dialog>
                <!-- Router view -->
                <router-view></router-view>
                <!-- FullScreen Speed Dial-->
                <fullscreen-speed-dial v-if="isAuthed" :value="!isIdle"></fullscreen-speed-dial>
            </template>
        </v-main>
        <!-- Footer -->
        <v-footer
            v-if="isAuthed && !isIdle"
            :height="20"
            app
            class="mb-1"
            color="white"
            padless
        >
            <div class="text-center text-subtitle-1 accent--text text--lighten-3" style="width: 100%">
                {{ APP_FOOTER }} - v{{ version }}
            </div>
        </v-footer>
    </v-app>
</template>

<script>
    import {mapObject} from 'underscore'
    import {mapActions, mapGetters} from 'vuex'
    import {APP_FOOTER, APP_TITLE} from '@/store/static/base-app'
    import connectionTypes from '@/store/static/connection-types'
    // Components
    import BaseAppLoading from '@windsor-telecom/customer-portal-framework/src/components/BaseAppLoading'
    import BaseConnectionMonitor from '@windsor-telecom/customer-portal-framework/src/components/BaseConnectionMonitor'
    import BaseDialog from '@windsor-telecom/customer-portal-framework/src/components/BaseDialog'
    import BaseError from '@windsor-telecom/customer-portal-framework/src/components/BaseError'
    import FullscreenSpeedDial from '@/components/FullscreenSpeedDial'

    export default {
        name: 'App',
        metaInfo: {
            title: 'Loading Wallboard',
            titleTemplate: `%s | ${APP_TITLE}`
        },
        data: () => ({
            APP_FOOTER,
            idleTime: 0,
            timerInterval: null,
            maxIdleTime: 3000,
            version: process.env.VERSION
        }),
        components: {
            BaseAppLoading,
            BaseConnectionMonitor,
            BaseDialog,
            BaseError,
            FullscreenSpeedDial
        },
        computed: {
            ...mapGetters('connections', [
                'connections',
                'hasMaxedAttempts',
                'hasLostConnection',
                'hasInitConnections',
                'connectionsError'
            ]),
            ...mapGetters('websocket', [
                'authError',
                'isAuthed',
                'loading',
                'connected'
            ]),
            appError () {
                const vm = this
                return vm.connectionsError || vm.authError
            },
            connectionTitle () {
                return (type) => {
                    switch (type) {
                        case connectionTypes.CC_WEBSOCKET:
                        default:
                            return 'Server Connection'
                    }
                }
            },
            connectionsFormatted () {
                const vm = this
                return mapObject(vm.connections, (connection, type) => ({
                    ...connection,
                    title: vm.connectionTitle(type)
                }))
            },
            isIdle () {
                const vm = this
                return vm.idleTime >= vm.maxIdleTime
            }
        },
        methods: {
            ...mapActions('auth', ['me']),
            handleConnectionsError (error) {
                const vm = this
                const message = 'Connection Lost, Max Reconnect Attempts Reached.'
                const context = {
                    error,
                    websocket: {
                        connected: vm.connected
                    }
                }

                // Send connection error to Sentry
                if (vm.$sentry) {
                    vm.$sentry.withScope((scope) => {
                        scope.setExtra('context', context)
                        vm.$sentry.captureMessage(message)
                    })
                }
            },
            resetIdleTimer () {
                const vm = this
                clearInterval(vm.timerInterval)
                vm.idleTime = 0
                vm.startIdleTimer()
            },
            startIdleTimer () {
                const vm = this
                const timeout = 1000

                vm.timerInterval = setInterval(() => {
                    if (vm.idleTime < vm.maxIdleTime) {
                        vm.idleTime += timeout
                        return
                    }

                    clearInterval(vm.timerInterval)
                    vm.timerInterval = null
                }, timeout)
            }
        },
        watch: {
            connectionsError (error) {
                if (error) {
                    this.handleConnectionsError(error)
                }
            }
        },
        created () {
            const vm = this
            const resetIdleTimer = vm.resetIdleTimer

            // Reset timer on user interaction
            window.onclick = resetIdleTimer
            window.onload = resetIdleTimer
            window.onmousedown = resetIdleTimer
            window.onmousemove = resetIdleTimer
            window.ontouchstart = resetIdleTimer

            // Start timer
            vm.startIdleTimer()
        }
    }
</script>
